// src/services/serviceSectionsService.js
//
// Frontend API wrapper for Service Sections in the Catalog domain.
//
// PURPOSE
// -------
// This service handles the second-level grouping of services inside a main category.
//
// Example:
//   Main category: Cosmetology
//   Sections:
//   - Eye area care
//   - Makeup
//   - Wax depilation
//   - Manicure and pedicure
//
// These sections are important because the current business / website structure
// is not flat. A service belongs to a section, and a section belongs to a main category.
//
// Logging policy and error style are intentionally aligned with visitService.js.
//

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ServiceSectionsService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch service sections.
 *
 * Backend:
 *   GET /catalog/service-sections
 *
 * Optional query params:
 *   - active=true|false
 *   - main_category_id=<id>
 *
 * Examples:
 *   fetchServiceSections()
 *   fetchServiceSections({ active: true })
 *   fetchServiceSections({ main_category_id: 5 })
 *
 * Return:
 * - expected: array of service section objects
 * - defensive fallback: [] if backend payload is missing / malformed
 */
export const fetchServiceSections = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/service-sections", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchServiceSections", err);
  }
};

/**
 * Create a new service section.
 *
 * Backend:
 *   POST /catalog/service-sections
 *
 * Expected body:
 *   {
 *     main_category_id: number,
 *     name_internal: string,
 *     name_public: string,
 *     slug?: string,
 *     intro_text?: string,
 *     notes_public?: string,
 *     sort_order?: number,
 *     is_active?: boolean,
 *     publish_to_website?: boolean
 *   }
 *
 * Notes:
 * - main_category_id is required because section must belong to a main category.
 */
export const createServiceSection = async (payload) => {
  try {
    if (!payload || typeof payload !== "object") {
      const e = new Error("createServiceSection: payload object is required.");
      e.code = "SS_PAYLOAD_MISSING";
      throw e;
    }

    if (
      payload.main_category_id === undefined ||
      payload.main_category_id === null ||
      `${payload.main_category_id}`.trim() === ""
    ) {
      const e = new Error("createServiceSection: main_category_id is required.");
      e.code = "SS_MAIN_CATEGORY_ID_MISSING";
      throw e;
    }

    if (!payload.name_internal || `${payload.name_internal}`.trim() === "") {
      const e = new Error("createServiceSection: name_internal is required.");
      e.code = "SS_NAME_INTERNAL_MISSING";
      throw e;
    }

    if (!payload.name_public || `${payload.name_public}`.trim() === "") {
      const e = new Error("createServiceSection: name_public is required.");
      e.code = "SS_NAME_PUBLIC_MISSING";
      throw e;
    }

    const res = await apiClient.post("/catalog/service-sections", payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createServiceSection", err);
  }
};

/**
 * Update an existing service section.
 *
 * Backend:
 *   PUT /catalog/service-sections/:id
 */
export const updateServiceSection = async (id, payload) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("updateServiceSection: id is required.");
      e.code = "SS_ID_MISSING";
      throw e;
    }

    if (!payload || typeof payload !== "object") {
      const e = new Error("updateServiceSection: payload object is required.");
      e.code = "SS_PAYLOAD_MISSING";
      throw e;
    }

    const res = await apiClient.put(`/catalog/service-sections/${id}`, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("updateServiceSection", err);
  }
};

/**
 * Toggle active state of a service section.
 *
 * Backend:
 *   PATCH /catalog/service-sections/:id/active
 *   Body: { active: true|false }
 */
export const toggleServiceSectionActive = async (id, active) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("toggleServiceSectionActive: id is required.");
      e.code = "SS_ID_MISSING";
      throw e;
    }

    const res = await apiClient.patch(
      `/catalog/service-sections/${id}/active`,
      { active: !!active },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("toggleServiceSectionActive", err);
  }
};

/**
 * Delete a service section.
 *
 * Backend:
 *   DELETE /catalog/service-sections/:id
 *
 * Backend may reject delete when:
 * - the section has publication history
 * - the section still contains child services
 */
export const deleteServiceSection = async (id) => {
  try {
    if (id === undefined || id === null || `${id}`.trim() === "") {
      const e = new Error("deleteServiceSection: id is required.");
      e.code = "SS_ID_MISSING";
      throw e;
    }

    const res = await apiClient.delete(`/catalog/service-sections/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteServiceSection", err);
  }
};

export default {
  fetchServiceSections,
  createServiceSection,
  updateServiceSection,
  toggleServiceSectionActive,
  deleteServiceSection,
};